// ============================================
// AlpenScreen Content Studio - Main Component
// File: frontend/src/components/ContentStudio/ContentStudio.js
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Plus,
  Grid,
  List,
  Search,
  Star,
  StarOff,
  Copy,
  Trash2,
  Edit,
  Loader,
  Sparkles,
  Monitor,
  Smartphone,
  RectangleHorizontal,
  Tv,
  X,
  Lock,
  Crown,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CanvasEditor from './CanvasEditor';
import TemplateGallery from './TemplateGallery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = API_URL.replace('/api', '');

// Canvas format presets (same as in CanvasEditor)
const CANVAS_FORMATS = [
  { id: '16:9', name: 'landscape169', label: '16:9 Full HD', width: 1920, height: 1080, icon: Monitor, description: '1920×1080' },
  { id: '16:9-4k', name: 'landscape4k', label: '16:9 4K', width: 3840, height: 2160, icon: Tv, description: '3840×2160' },
  { id: '4:3', name: 'landscape43', label: '4:3 Landscape', width: 1440, height: 1080, icon: Monitor, description: '1440×1080' },
  { id: '9:16', name: 'portrait', label: '9:16 Portrait', width: 1080, height: 1920, icon: Smartphone, description: '1080×1920' },
  { id: '21:9', name: 'ultrawide', label: '21:9 Ultrawide', width: 2560, height: 1080, icon: RectangleHorizontal, description: '2560×1080' }
];

const ContentStudio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State
  const [view, setView] = useState('gallery'); // 'gallery', 'designs', 'editor'
  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [blankDesignFormat, setBlankDesignFormat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showNewDesignModal, setShowNewDesignModal] = useState(false);
  
  // Access control state
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentTier, setCurrentTier] = useState(null);

  // Check access on mount
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/designs/access/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setHasAccess(data.hasAccess);
          setCurrentTier(data.tier);
        }
      } catch (error) {
        console.error('Error checking Content Studio access:', error);
        setHasAccess(false);
      } finally {
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, []);

  // Fetch user designs
  const fetchDesigns = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (showFavoritesOnly) params.append('favorite', 'true');
      
      const response = await fetch(`${API_URL}/designs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDesigns(data.designs || []);
      }
    } catch (error) {
      console.error('Error fetching designs:', error);
    } finally {
      setLoading(false);
    }
  }, [showFavoritesOnly]);

  useEffect(() => {
    if (view === 'designs') {
      fetchDesigns();
    }
  }, [view, fetchDesigns]);

  // Create new design from template
  const handleCreateFromTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/templates/${template.slug}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'de'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedTemplate(data.template);
        setSelectedDesign(null);
        setBlankDesignFormat(null);
        setView('editor');
      }
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  // Create blank design with selected format
  const handleCreateBlankDesign = (format) => {
    setBlankDesignFormat(format);
    setSelectedTemplate(null);
    setSelectedDesign(null);
    setShowNewDesignModal(false);
    setView('editor');
  };

  // Edit existing design
  const handleEditDesign = (design) => {
    setSelectedDesign(design);
    setSelectedTemplate(null);
    setBlankDesignFormat(null);
    setView('editor');
  };

 // Save design
  const handleSaveDesign = async (designData) => {
    try {
      const token = localStorage.getItem('token');
      const method = selectedDesign ? 'PUT' : 'POST';
      const url = selectedDesign 
        ? `${API_URL}/designs/${selectedDesign.id}`
        : `${API_URL}/designs`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: designData.name,
          canvasData: designData.canvasData,
          thumbnail: designData.thumbnail,  // ← NEU: Thumbnail mitsenden!
          templateId: selectedTemplate?.id,
          width: designData.width || 1920,
          height: designData.height || 1080
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedDesign(data.design);
        fetchDesigns();
        return data.design;
      }
    } catch (error) {
      console.error('Error saving design:', error);
    }
    return null;
  };

  // Duplicate design
  const handleDuplicateDesign = async (design) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/designs/${design.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchDesigns();
      }
    } catch (error) {
      console.error('Error duplicating design:', error);
    }
  };

  // Toggle favorite
  const handleToggleFavorite = async (design) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/designs/${design.id}/favorite`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDesigns();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Delete design
  const handleDeleteDesign = async (design) => {
    if (!window.confirm(t('contentStudio.confirmDelete', { name: design.name }))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/designs/${design.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDesigns();
      }
    } catch (error) {
      console.error('Error deleting design:', error);
    }
  };

  // Close editor
  const handleCloseEditor = () => {
    setSelectedDesign(null);
    setSelectedTemplate(null);
    setBlankDesignFormat(null);
    setView('designs');
  };

  // Filter designs
  const filteredDesigns = designs.filter(design =>
    design.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render editor view
  if (view === 'editor') {
    // Create a blank template object if creating from scratch
    const templateForEditor = blankDesignFormat 
      ? {
          name: t('contentStudio.newDesign', 'Neues Design'),
          width: blankDesignFormat.width,
          height: blankDesignFormat.height,
          canvas_data: {
            version: '6.0.0',
            objects: [],
            backgroundColor: '#1a1a2e'
          }
        }
      : selectedTemplate;

    return (
      <CanvasEditor
        template={templateForEditor}
        design={selectedDesign}
        onSave={handleSaveDesign}
        onClose={handleCloseEditor}
      />
    );
  }

  // Loading state while checking access
  if (!accessChecked) {
    return (
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center py-20">
          <Loader className="animate-spin text-purple-600" size={32} />
        </div>
      </div>
    );
  }

  // Upgrade required screen
  if (!hasAccess) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-12 text-center text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
              <Crown size={32} />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              {t('contentStudio.title', 'Content Studio')}
            </h1>
            <p className="text-purple-100 text-lg">
              {t('contentStudio.upgrade.premiumFeature', 'Premium Feature für Business & Enterprise')}
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Current tier badge */}
            <div className="flex items-center justify-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-full text-sm">
                <Lock size={16} className="text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {t('contentStudio.upgrade.currentTier', 'Aktueller Plan')}: 
                </span>
                <span className="font-semibold text-gray-900 dark:text-white capitalize">
                  {currentTier || 'Starter'}
                </span>
              </span>
            </div>

            {/* Description */}
            <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
              {t('contentStudio.upgrade.description', 'Erstellen Sie professionelle Inhalte mit dem Canvas-Editor, Unsplash-Integration und vorgefertigten Vorlagen. Upgraden Sie auf Business oder Enterprise um Zugang zu erhalten.')}
            </p>

            {/* Features list */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                { icon: '🎨', text: t('contentStudio.upgrade.feature1', 'Professioneller Canvas-Editor') },
                { icon: '📷', text: t('contentStudio.upgrade.feature2', 'Unsplash Bildergalerie') },
                { icon: '📐', text: t('contentStudio.upgrade.feature3', 'Mehrere Formate (4K, Portrait, etc.)') },
                { icon: '📁', text: t('contentStudio.upgrade.feature4', 'Vorlagen für verschiedene Branchen') },
                { icon: '💾', text: t('contentStudio.upgrade.feature5', 'Designs speichern & exportieren') },
                { icon: '🖼️', text: t('contentStudio.upgrade.feature6', 'Direkt als Playlist-Media nutzen') }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <span className="text-xl">{feature.icon}</span>
                  <span className="text-gray-700 dark:text-gray-300">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Pricing comparison */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">
                {t('contentStudio.upgrade.includedIn', 'Enthalten in')}:
              </h3>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <div className="flex items-center gap-2 justify-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="font-medium text-gray-900 dark:text-white">Business</span>
                  <span className="text-gray-500 dark:text-gray-400">CHF 99/Mt.</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="font-medium text-gray-900 dark:text-white">Enterprise</span>
                  <span className="text-gray-500 dark:text-gray-400">CHF 299/Mt.</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
              >
                {t('contentStudio.upgrade.cta', 'Jetzt upgraden')}
                <ArrowRight size={20} />
              </button>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                {t('contentStudio.upgrade.trialNote', '14 Tage kostenlos testen')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Sparkles className="text-purple-500" size={28} />
            {t('contentStudio.title', 'Content Studio')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('contentStudio.subtitle', 'Erstellen Sie professionelle Inhalte für Ihre Screens')}
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('gallery')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              view === 'gallery'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {t('contentStudio.templates', 'Vorlagen')}
          </button>
          <button
            onClick={() => setView('designs')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              view === 'designs'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {t('contentStudio.myDesigns', 'Meine Designs')}
          </button>
        </div>
      </div>

      {/* Template Gallery View */}
      {view === 'gallery' && (
        <div>
          {/* New Design Button - Prominent placement */}
          <div className="mb-8">
            <button
              onClick={() => setShowNewDesignModal(true)}
              className="w-full md:w-auto flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Plus size={24} />
              <div className="text-left">
                <span className="font-semibold text-lg">{t('contentStudio.createBlankDesign', 'Leeres Design erstellen')}</span>
                <p className="text-sm text-purple-200">{t('contentStudio.startFromScratch', 'Von Grund auf neu beginnen')}</p>
              </div>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
            <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
              {t('contentStudio.orChooseTemplate', 'oder wählen Sie eine Vorlage')}
            </span>
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
          </div>

          {/* Template Gallery */}
          <TemplateGallery onSelectTemplate={handleCreateFromTemplate} />
        </div>
      )}

      {/* My Designs View */}
      {view === 'designs' && (
        <div>
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('contentStudio.searchDesigns', 'Designs durchsuchen...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Favorites filter */}
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`p-2 rounded-lg transition-colors ${
                  showFavoritesOnly
                    ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                title={t('contentStudio.showFavorites', 'Favoriten anzeigen')}
              >
                <Star size={20} fill={showFavoritesOnly ? 'currentColor' : 'none'} />
              </button>

              {/* View mode */}
              <div className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}`}
                >
                  <Grid size={20} className="text-gray-600 dark:text-gray-400" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}`}
                >
                  <List size={20} className="text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* New design button */}
              <button
                onClick={() => setShowNewDesignModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus size={20} />
                <span className="hidden md:inline">{t('contentStudio.newDesign', 'Neues Design')}</span>
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-12">
              <Loader className="animate-spin text-purple-600" size={32} />
            </div>
          )}

          {/* Empty state */}
          {!loading && filteredDesigns.length === 0 && (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <Layout className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm 
                  ? t('contentStudio.noResults', 'Keine Designs gefunden')
                  : t('contentStudio.noDesigns', 'Noch keine Designs')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('contentStudio.startWithTemplate', 'Starten Sie mit einer Vorlage oder erstellen Sie ein leeres Design')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => setShowNewDesignModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus size={20} />
                  {t('contentStudio.createBlankDesign', 'Leeres Design erstellen')}
                </button>
                <button
                  onClick={() => setView('gallery')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('contentStudio.browseTemplates', 'Vorlagen durchsuchen')}
                </button>
              </div>
            </div>
          )}

          {/* Designs Grid */}
          {!loading && filteredDesigns.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDesigns.map(design => (
                <DesignCard
                  key={design.id}
                  design={design}
                  onEdit={handleEditDesign}
                  onDuplicate={handleDuplicateDesign}
                  onToggleFavorite={handleToggleFavorite}
                  onDelete={handleDeleteDesign}
                />
              ))}
            </div>
          )}

          {/* Designs List */}
          {!loading && filteredDesigns.length > 0 && viewMode === 'list' && (
            <div className="space-y-2">
              {filteredDesigns.map(design => (
                <DesignListItem
                  key={design.id}
                  design={design}
                  onEdit={handleEditDesign}
                  onDuplicate={handleDuplicateDesign}
                  onToggleFavorite={handleToggleFavorite}
                  onDelete={handleDeleteDesign}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Design Modal */}
      {showNewDesignModal && (
        <NewDesignModal
          formats={CANVAS_FORMATS}
          onSelect={handleCreateBlankDesign}
          onClose={() => setShowNewDesignModal(false)}
        />
      )}
    </div>
  );
};

// New Design Modal Component
const NewDesignModal = ({ formats, onSelect, onClose }) => {
  const { t } = useTranslation();
  const [selectedFormat, setSelectedFormat] = useState(formats[0]);

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('contentStudio.createNewDesign', 'Neues Design erstellen')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('contentStudio.selectFormatDescription', 'Wählen Sie das passende Format für Ihren Bildschirm')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Format Options */}
        <div className="space-y-3 mb-6">
          {formats.map((format) => {
            const Icon = format.icon;
            const isActive = selectedFormat.id === format.id;
            
            return (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format)}
                className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                  isActive
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                }`}
              >
                <div className={`p-3 rounded-lg ${isActive ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-gray-100 dark:bg-gray-700'}`}>
                  <Icon size={24} className={isActive ? 'text-purple-600' : 'text-gray-600 dark:text-gray-400'} />
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${isActive ? 'text-purple-600' : 'text-gray-900 dark:text-white'}`}>
                    {format.label}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format.description}
                  </p>
                </div>
                {isActive && (
                  <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Preview indicator */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {t('contentStudio.selectedFormat', 'Ausgewähltes Format')}:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedFormat.label} ({selectedFormat.description})
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            onClick={() => onSelect(selectedFormat)}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <Plus size={20} />
            {t('contentStudio.createDesign', 'Design erstellen')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Design Card Component
const DesignCard = ({ design, onEdit, onDuplicate, onToggleFavorite, onDelete }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-lg transition-all group">
      {/* Thumbnail */}
      <div 
        className="aspect-video bg-gray-100 dark:bg-gray-900 relative cursor-pointer"
        onClick={() => onEdit(design)}
      >
        {design.thumbnail_url ? (
          <img 
            src={`${API_BASE_URL}${design.thumbnail_url}`}
            alt={design.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layout className="text-gray-400" size={48} />
          </div>
        )}
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(design); }}
            className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
            title={t('common.edit')}
          >
            <Edit size={20} className="text-gray-700" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(design); }}
            className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
            title={t('common.duplicate')}
          >
            <Copy size={20} className="text-gray-700" />
          </button>
        </div>

        {/* Favorite badge */}
        {design.is_favorite && (
          <div className="absolute top-2 right-2">
            <Star className="text-yellow-500 fill-yellow-500" size={20} />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 dark:text-white truncate">
          {design.name}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {new Date(design.updated_at).toLocaleDateString('de-CH')}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => onToggleFavorite(design)}
            className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
          >
            {design.is_favorite ? (
              <Star size={18} className="fill-yellow-500 text-yellow-500" />
            ) : (
              <StarOff size={18} />
            )}
          </button>
          <button
            onClick={() => onDelete(design)}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Design List Item Component
const DesignListItem = ({ design, onEdit, onDuplicate, onToggleFavorite, onDelete }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all">
      {/* Thumbnail */}
      <div 
        className="w-32 h-20 bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
        onClick={() => onEdit(design)}
      >
        {design.thumbnail_url ? (
          <img 
            src={`${API_BASE_URL}${design.thumbnail_url}`}
            alt={design.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layout className="text-gray-400" size={24} />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-900 dark:text-white truncate">
            {design.name}
          </h3>
          {design.is_favorite && (
            <Star className="text-yellow-500 fill-yellow-500 flex-shrink-0" size={16} />
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('contentStudio.lastEdited', 'Zuletzt bearbeitet')}: {new Date(design.updated_at).toLocaleDateString('de-CH')}
        </p>
        {design.template_name && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {t('contentStudio.basedOn', 'Basiert auf')}: {design.template_name}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(design)}
          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title={t('common.edit')}
        >
          <Edit size={18} />
        </button>
        <button
          onClick={() => onDuplicate(design)}
          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title={t('common.duplicate')}
        >
          <Copy size={18} />
        </button>
        <button
          onClick={() => onToggleFavorite(design)}
          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title={t('contentStudio.toggleFavorite', 'Favorit umschalten')}
        >
          {design.is_favorite ? (
            <Star size={18} className="fill-yellow-500 text-yellow-500" />
          ) : (
            <StarOff size={18} />
          )}
        </button>
        <button
          onClick={() => onDelete(design)}
          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 rounded-lg transition-colors"
          title={t('common.delete')}
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

export default ContentStudio;